import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import { rangeProducts, warrantyFor } from '../data/mock';
import { EMS_STATUS, DISPATCH_NOTE, DELIVERY_NOTE } from '../data/availability';
import { startCheckout, hasLink } from '../lib/checkout';
import ProductGallery from './ProductGallery';
import { toast } from 'sonner';
import Footer from './Footer';

const ShopAll = () => {
  const handleCheckout = (item) => {
    try {
      const route = startCheckout(item, 'machine');
      if (route === 'enquiry') {
        toast.success('Opening WhatsApp so we can confirm your order and delivery.');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Something went wrong. Please contact us and we will take the order directly.');
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-md border-b border-cyan-500/20">
        <nav className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <a
              href="/"
              className="flex items-center space-x-3 text-gray-400 hover:text-cyan-400 transition-colors"
              data-testid="shop-all-back-home"
            >
              <ArrowLeft size={20} />
              <span className="text-sm">Back to Home</span>
            </a>
            <span className="text-2xl font-bold">
              <span className="text-cyan-400 neon-text">EFAM</span>
              <span className="text-white ml-2 text-lg align-middle">FULL RANGE</span>
            </span>
          </div>
        </nav>
      </header>

      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
      </div>

      <main className="pt-32 pb-20 relative z-10">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" data-testid="shop-all-title">
              The Full <span className="text-cyan-400 neon-text">Range</span>
            </h1>
            <p className="text-lg text-gray-400">
              24 configurations, from the SoloForge single-paddle unit to the Crown Jewel flagship.
            </p>
            <p className="mt-3 text-sm text-cyan-400 font-semibold" data-testid="ems-availability">
              {EMS_STATUS} &middot; {DISPATCH_NOTE} &middot; {DELIVERY_NOTE}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Click any photo to enlarge it and see every angle. Model names are range names &mdash; check the finish for the actual colour.
            </p>
          </div>

          <div
            className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-7xl mx-auto"
            data-testid="shop-all-grid"
          >
            {rangeProducts.map((item) => (
              <div
                key={item.id}
                className="glass-card rounded-xl overflow-hidden border border-slate-700 hover:border-cyan-500/50 transition-all duration-300 flex flex-col"
                data-testid={`range-card-${item.id}`}
              >
                <ProductGallery item={item} className="h-44" />

                <div className="p-5 space-y-3 flex flex-col flex-1">
                  <div className="text-center flex-1">
                    <h3 className="text-base font-bold text-cyan-400 leading-snug">
                      {item.name}
                    </h3>
                    {item.finish && (
                      <p
                        className="mt-1.5 inline-block rounded-full border border-slate-600 px-2.5 py-0.5 text-[11px] text-gray-300"
                        data-testid={`finish-${item.id}`}
                      >
                        Finish: <span className="font-semibold text-white">{item.finish}</span>
                      </p>
                    )}
                    <p className="text-gray-500 text-xs mt-2">
                      {item.subtitle}
                    </p>
                    <p className="text-gray-600 text-[11px] mt-2">
                      {warrantyFor(item.priceAud)} year warranty &middot; {DISPATCH_NOTE}
                    </p>
                  </div>

                  <Button
                    className="w-full bg-cyan-500 hover:bg-cyan-400 text-black font-bold rounded-full py-5"
                    onClick={() => handleCheckout(item)}
                    data-testid={`range-checkout-btn-${item.id}`}
                  >
                    <div className="flex flex-col items-center">
                      <span className="text-[11px] font-normal">
                        {hasLink('machine', item.id) ? 'Buy now' : 'Enquire to order'}
                      </span>
                      <span className="text-base font-black">{item.price}</span>
                    </div>
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-gray-500 text-sm mt-12 max-w-2xl mx-auto">
            Not sure which configuration is right for you? <a href="/#contact" className="text-cyan-400 hover:underline">Get in touch</a> and
            we'll help you choose.
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ShopAll;
